import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Button } from '../ui/button'
import { Progress } from '../ui/progress'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { companyAPI } from '../../services/api'
import { toast } from '../../hooks/use-toast'
import {
  Upload,
  FileText,
  AlertCircle,
  Loader2,
  ExternalLink,
  Info,
  Video,
  LinkIcon
} from 'lucide-react'

export default function PitchDeckSubmissionModal({ open, onClose }) {
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [existingFeedback, setExistingFeedback] = useState(null)

  // URL states
  const [documentUrl, setDocumentUrl] = useState('')
  const [videoUrl, setVideoUrl] = useState('')

  // Validation states
  const [errors, setErrors] = useState({})

  // Fetch existing feedback when modal opens
  useEffect(() => {
    if (open) {
      fetchExistingFeedback()
    } else {
      // Reset state when modal closes
      resetForm()
    }
  }, [open])

  const fetchExistingFeedback = async () => {
    setFetchingData(true)
    try {
      const response = await companyAPI.getPitchDeckFeedback()
      if (response.data.success && response.data.feedback) {
        setExistingFeedback(response.data.feedback)

        // Set existing URLs
        if (response.data.feedback.documentUrl) {
          setDocumentUrl(response.data.feedback.documentUrl)
        }
        if (response.data.feedback.videoUrl) {
          setVideoUrl(response.data.feedback.videoUrl)
        }
      }
    } catch (error) {
      console.log('No existing pitch deck feedback found')
    } finally {
      setFetchingData(false)
    }
  }

  const resetForm = () => {
    setDocumentUrl('')
    setVideoUrl('')
    setErrors({})
    setUploadProgress(0)
  }

  const isValidUrl = (urlString) => {
    try {
      new URL(urlString)
      return true
    } catch (e) {
      return false
    }
  }

  const handleSubmit = async () => {
    // Validate at least one URL is present
    if (!documentUrl && !videoUrl) {
      setErrors({ general: 'Please provide at least one link (document or video)' })
      return
    }

    // Validate URLs
    if (documentUrl && !isValidUrl(documentUrl)) {
      setErrors({ general: 'Please provide a valid document URL' })
      return
    }

    if (videoUrl && !isValidUrl(videoUrl)) {
      setErrors({ general: 'Please provide a valid video URL' })
      return
    }

    setLoading(true)
    setUploadProgress(0)

    try {
      const formData = new FormData()

      // Append URLs
      formData.append('documentUrl', documentUrl)
      formData.append('videoUrl', videoUrl)

      // Keep existing presenter info if available
      if (existingFeedback) {
        // Include existing presenter data to preserve it
        formData.append('presenterFirstName', existingFeedback.presenterFirstName || '')
        formData.append('presenterLastName', existingFeedback.presenterLastName || '')
        formData.append('presenterTitle', existingFeedback.presenterTitle || '')
        formData.append('presenterCountryCode', existingFeedback.presenterCountryCode || '91')
        formData.append('presenterMobile', existingFeedback.presenterMobile || '')
        formData.append('presenterEmail', existingFeedback.presenterEmail || '')
        formData.append('presenterLinkedIn', existingFeedback.presenterLinkedIn || '')
        formData.append('presenterCompanyName', existingFeedback.presenterCompanyName || '')
      }

      // Track upload progress
      const config = {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setUploadProgress(percentCompleted)
        }
      }

      const response = existingFeedback
        ? await companyAPI.updatePitchDeckFeedback(formData, config)
        : await companyAPI.submitPitchDeckFeedback(formData, config)

      if (response.data.success) {
        toast.success('Pitch deck and video links submitted successfully!')
        resetForm()
        onClose()
      }
    } catch (error) {
      console.error('Error submitting pitch deck:', error)
      setErrors({ general: 'Failed to submit. Please try again.' })
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] !flex !flex-col !overflow-hidden !p-0 [&>button]:top-6 [&>button]:right-6">
        <DialogHeader className="flex-shrink-0 p-6 pb-0 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Pitch Deck & Video Submission</DialogTitle>
              <DialogDescription className="mt-1 pb-4">
                Submit your presentation deck and demo video links
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {fetchingData ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* AI Review Assistant Section */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">AI Pitch Review Assistant</h4>
                      <p className="text-sm text-gray-600">
                        Get instant feedback on your pitch deck before submission. Our AI assistant helps improve structure, content clarity, and presentation quality.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button
                    type="button"
                    onClick={() => window.open('https://chatgpt.com/g/g-68d395308cec8191bdfbe7fbaf93cb33-ifta-2025-demo-company-pitch-review-helper', '_blank', 'noopener,noreferrer')}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
                  >
                    <span>Open AI Assistant</span>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700">{errors.general}</span>
              </div>
            )}

            {/* Document Link Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b pb-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-primary">Pitch Deck Document</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="documentUrl" className="text-sm font-medium flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Document Link (PDF or PPTX)
                </Label>
                <Input
                  id="documentUrl"
                  type="url"
                  placeholder="https://drive.google.com/file/d/..."
                  value={documentUrl}
                  onChange={(e) => setDocumentUrl(e.target.value)}
                  className="h-10"
                />
                <p className="text-xs text-muted-foreground">
                  Provide a publicly accessible link to your pitch deck (Google Drive, Dropbox, etc.)
                </p>
              </div>
            </div>

            {/* Video Link Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 border-b pb-2">
                <Video className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-primary">Demo Video</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="videoUrl" className="text-sm font-medium flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Video Link
                </Label>
                <Input
                  id="videoUrl"
                  type="url"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="h-10"
                />
                <p className="text-xs text-muted-foreground">
                  Provide a publicly accessible link to your demo video (YouTube, Vimeo, Google Drive, etc.)
                </p>
              </div>
            </div>

            {/* Requirements Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">Submission Guidelines</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>Links must be publicly accessible (no login required)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>At least one link (document or video) is required</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-400 mt-1">•</span>
                      <span>You can update these links anytime before the deadline</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Submitting...</span>
                  <span className="font-medium">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-shrink-0 border-t p-6 bg-background shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.05)]">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || (!documentUrl && !videoUrl)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : existingFeedback?.documentUrl || existingFeedback?.videoUrl ? (
              <>
                <Upload className="h-4 w-4" />
                Update Submission
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Submit
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}